import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { JwtPayload, Permission } from '../types';
import { setAccessToken, getAccessToken } from '../lib/api';
import { queryClient } from '../index';

interface AuthState {
  user: JwtPayload | null;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  receiveToken: (token: string) => JwtPayload;
  logout: () => void;
  isAuthenticated: boolean;
  hasPermission: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

function decodeToken(token: string): JwtPayload {
  const payload = token.split('.')[1];
  return JSON.parse(atob(payload));
}

function isTokenExpired(payload: JwtPayload): boolean {
  return payload.exp * 1000 < Date.now();
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const token = getAccessToken();
    if (token) {
      try {
        const payload = decodeToken(token);
        if (!isTokenExpired(payload)) {
          return { user: payload, isLoading: false };
        }
      } catch {
        // malformed token
      }
      setAccessToken(null);
    }
    return { user: null, isLoading: false };
  });

  const receiveToken = useCallback((token: string): JwtPayload => {
    queryClient.clear();
    setAccessToken(token);
    const user = decodeToken(token);
    setState({ user, isLoading: false });
    return user;
  }, []);

  const logout = useCallback(() => {
    queryClient.clear();
    setAccessToken(null);
    setState({ user: null, isLoading: false });
  }, []);

  useEffect(() => {
    if (!state.user) return;

    let idleTimer: ReturnType<typeof setTimeout>;
    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(logout, IDLE_TIMEOUT_MS);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') resetIdleTimer();
    };

    const activityEvents: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach((event) => window.addEventListener(event, resetIdleTimer, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibilityChange);
    resetIdleTimer();

    return () => {
      clearTimeout(idleTimer);
      activityEvents.forEach((event) => window.removeEventListener(event, resetIdleTimer));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state.user, logout]);

  const hasPermission = useCallback((permission: Permission): boolean => {
    return state.user?.permissions.includes(permission) ?? false;
  }, [state.user]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        receiveToken,
        logout,
        isAuthenticated: !!state.user,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
